/**
 * Shared retro-aesthetic components used across the Share and Settings UI.
 *
 * CopyableField  — read-only text field with a copy-icon adornment and
 *                  check-icon success micro-interaction.
 * SectionHeader  — JetBrains Mono uppercase heading with optional amber
 *                  primary accent and ▸ glyph.
 *
 * Aesthetic: JetBrains Mono · amber accent · 8dp spacing rhythm.
 * Accessibility: every copy button carries an aria-label; the icon swap
 *   is announced via the parent's aria-live region (not a toast).
 * Motion: colour transition uses 200 ms ease; no layout-affecting animations.
 */

import type React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// ---------------------------------------------------------------------------
// SectionHeader — retro uppercase monospace heading
// ---------------------------------------------------------------------------

export interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  /** When true: amber primary colour + ▸ glyph + slightly larger font. */
  primary?: boolean;
}

export function SectionHeader({
  title,
  subtitle,
  primary = false,
}: SectionHeaderProps) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1.5}>
      <Typography
        component="h2"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: primary ? '0.875rem' : '0.75rem',
          fontWeight: primary ? 700 : 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: primary ? 'primary.main' : 'text.primary',
        }}
      >
        {primary ? '▸ ' : ''}
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'text.secondary',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// CopyableField — read-only text field + copy/check icon adornment
// ---------------------------------------------------------------------------

export interface CopyableFieldProps {
  value: string;
  /** Accessible description used in the copy button's aria-label. */
  ariaLabel: string;
  onCopy: () => void;
  /** When true: the copy icon swaps to a check icon (success state). */
  copied: boolean;
  multiline?: boolean;
  /** Apply JetBrains Mono + font-size 12 to the input text. */
  monospace?: boolean;
  copyButtonRef?: React.Ref<HTMLButtonElement>;
}

export function CopyableField({
  value,
  ariaLabel,
  onCopy,
  copied,
  multiline = false,
  monospace = false,
  copyButtonRef,
}: CopyableFieldProps) {
  return (
    <TextField
      value={value}
      fullWidth
      multiline={multiline}
      maxRows={multiline ? 8 : 1}
      InputProps={{
        readOnly: true,
        sx: monospace
          ? { fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }
          : undefined,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={`Copy ${ariaLabel.toLowerCase()}`}
              onClick={onCopy}
              ref={copyButtonRef}
              size="large"
              sx={{
                minWidth: 44,
                minHeight: 44,
                color: copied ? 'success.main' : 'text.secondary',
                transition: 'color 200ms ease',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {copied ? (
                <CheckIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
      inputProps={{
        'aria-label': ariaLabel,
      }}
    />
  );
}
